import { Button, Collapse, Icon, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import { TaskResource } from 'dy-frontend-http-repository/lib/modules/OpenTaskList/resources';
import { useStore } from 'effector-react';
import React, { useEffect, useState } from 'react';
import { coreUserOpenTaskListDataApi } from './store/apis';
import { fetchCoreUserOpenTaskList } from './store/effects';
import { resetCoreUserOpenTaskListData } from './store/events';
import { $coreUserOpenTaskListData } from './store/states';
import NonIdealState from '@app/components/NonIdealState';
import { useNavigate } from 'react-router-dom';
import { Endpoints } from '@app/data/consts';
import qs from 'qs';
import { $coreUser } from '../../store/states';
import OpenTaskCard from '@app/containers/components/OpenTaskCard';
import { OpenTaskType } from 'dy-frontend-shared/lib/data/enums';
import { usePageTitle } from '@app/hooks';
import { $permissions } from '@containers/store/states';
import { ToastUtils } from '@app/data/utils';
import { HTTPErrorType } from 'dy-frontend-http-repository/lib/data/enums';
import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';

const CoreUserTasks: React.FC = () => {
    const permissions = useStore($permissions);

    const { changeTitle } = usePageTitle('Member Tasks');

    const navigate = useNavigate();
    const coreUser = useStore($coreUser);

    const coreUserOpenTaskListData = useStore($coreUserOpenTaskListData);

    const [isPausedTaskListCollapsed, setIsPauseListCollapsed] = useState(true);

    useEffect(() => {
        if (!coreUser) {
            return;
        }

        if (!permissions.isEnabled.task) {
            return;
        }

        fetchCoreUserOpenTaskList({ filter: { user_id: coreUser.id } })
            .then((openTaskList) => coreUserOpenTaskListDataApi.initialize({ openTaskList }))
            .catch((e) => {
                // Log
                console.error(e);

                const response = (e as any).response as HTTPErrorResponse;
                if (response.data.type === HTTPErrorType.MISSING) {
                    // Show error message
                    ToastUtils.showToast({
                        message: `Open requests for member with ID of ${coreUser.id} was not found`,
                        intent: Intent.DANGER,
                    });

                    // Go to core users page
                    navigate(Endpoints.TASKS);
                }
            });

        return () => {
            resetCoreUserOpenTaskListData();
        };

        // eslint-disable-next-line
    }, [coreUser?.id]);

    useEffect(() => {
        if (!coreUser) {
            return;
        }

        changeTitle(`Member Requests - ${coreUser.first_name} ${coreUser.last_name}`);
    }, [coreUser?.first_name, coreUser?.last_name]);

    if (!coreUser) {
        return null;
    }

    if (!permissions.isEnabled.task) {
        return null;
    }

    if (!coreUserOpenTaskListData) {
        // Core user open task list data is NOT fetched yet
        return (
            <Flex justify="center">
                <Spinner />
            </Flex>
        );
    }

    const renderHeader = () => {
        const handleNavigateToAllCoreUserTasks = () => {
            // TODO: use task filter function to get right value or add new for client & core user tasks
            navigate(`${Endpoints.TASKS}?${qs.stringify({ page: 1, participantIds: [coreUser.id] })}`);
        };

        return (
            <Flex className="mb-2" justify="space-between" align="center">
                <Heading type="h3">Current user requests</Heading>

                <Button intent={Intent.PRIMARY} onClick={handleNavigateToAllCoreUserTasks}>
                    View all requests
                </Button>
            </Flex>
        );
    };

    const renderTasksList = (tasks: TaskResource[]) => {
        return tasks.map((task, index) => (
            <OpenTaskCard
                key={task.id}
                className={index === 0 ? '' : 'mt-1'}
                task={task}
                taskData={coreUserOpenTaskListData}
            />
        ));
    };

    const renderPausedTasks = (tasks: TaskResource[], isForceOpen = false) => {
        if (tasks.length === 0) {
            return null;
        }

        return (
            <div>
                <Flex className={isPausedTaskListCollapsed && !isForceOpen ? '' : 'mb-1'} align="center"
                      justify="space-between">
                    <Heading type="h3">
                        {isPausedTaskListCollapsed && !isForceOpen ? `Paused: ${tasks.length} request(s)` : 'Paused'}
                    </Heading>
                    {!isForceOpen && (
                        <Button
                            minimal
                            icon={isPausedTaskListCollapsed ? 'chevron-down' : 'chevron-up'}
                            onClick={() => setIsPauseListCollapsed(!isPausedTaskListCollapsed)}
                        >{isPausedTaskListCollapsed ? 'Click to show' : 'Click to hide'}</Button>
                    )}
                </Flex>

                <Collapse isOpen={isForceOpen || !isPausedTaskListCollapsed}>
                    {renderTasksList(tasks)}
                </Collapse>
            </div>
        );
    };

    const renderActiveTasks = (tasks: TaskResource[]) => {
        if (tasks.length === 0) {
            return null;
        }

        return (
            <div>
                <Heading className="mb-1" type="h3">
                    Active
                </Heading>
                {renderTasksList(tasks)}
            </div>
        );
    };

    const renderBacklogTasks = (tasks: TaskResource[]) => {
        if (tasks.length === 0) {
            return null;
        }

        return (
            <div>
                <Heading className="mb-1" type="h3">
                    Backlog
                </Heading>
                {renderTasksList(tasks)}
            </div>
        );
    };

    const renderOpenTasks = () => {
        const activeTasks = Object.values(coreUserOpenTaskListData.tasks[OpenTaskType.ACTIVE]);
        const backlogTasks = Object.values(coreUserOpenTaskListData.tasks[OpenTaskType.BACKLOG]);
        const pausedTasks = Object.values(coreUserOpenTaskListData.tasks[OpenTaskType.PAUSED]);

        const isTaskListEmpty = ![activeTasks, backlogTasks, pausedTasks].some((tasks) => tasks.length > 0);
        if (isTaskListEmpty) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            User do not participate in any requests
                        </Heading>
                    }
                />
            );
        }

        return (
            <div>
                <div className="mb-3">{renderPausedTasks(
                    pausedTasks,
                    activeTasks.length === 0 && backlogTasks.length === 0,
                )}</div>
                <div className="mb-3">{renderActiveTasks(activeTasks)}</div>
                <div>{renderBacklogTasks(backlogTasks)}</div>
            </div>
        );
    };

    return (
        <div>
            {renderHeader()}
            {renderOpenTasks()}
        </div>
    );
};

export default CoreUserTasks;
